// Read-only stand-in for PlatformFormatPicker in scene-reel mode. A scene reel
// has a fixed output set (from GET /api/ads/formats `workflows[].outputs`), so
// there is nothing to tick; the picker's own state (the ticked surfaces) is
// left untouched underneath and comes back unchanged on "Standard ads".
//
// Copy rules: see WorkflowPicker.tsx — no "size", no "master".

import { Box, Flex, HStack, Text, Badge } from '@chakra-ui/react';
import type { WorkflowEntry } from './formatCatalog';
import { cutLabel } from '../../lib/sceneReel';

function aspectWord(aspect: string): string {
  return aspect === '9:16' ? 'Vertical'
    : aspect === '16:9' ? 'Landscape'
    : aspect === '1:1' ? 'Square'
    : aspect;
}

export function SceneReelOutputsCard({ workflow }: { workflow: WorkflowEntry }) {
  const source = workflow.outputs.find(o => !o.derived && o.aspect === '9:16')
    || workflow.outputs.find(o => !o.derived);
  return (
    <Box borderWidth="1px" borderColor="brand.border" borderRadius="lg" bg="brand.surface" p={4}>
      <HStack justify="space-between" align="baseline" mb={3}>
        <Text fontSize="11px" fontWeight="700" textTransform="uppercase" letterSpacing="0.06em" color="brand.muted">
          Scene reel outputs
        </Text>
        <Text fontSize="10px" color="brand.muted">
          per product · {workflow.durationSec} s · {cutLabel(workflow.cut)} · straight cuts
        </Text>
      </HStack>
      <Flex gap={2} wrap="wrap">
        {workflow.outputs.map(o => (
          <Box
            key={o.aspect}
            flex="1 1 160px"
            px={3}
            py={2}
            borderWidth="1px"
            borderColor="brand.border"
            borderRadius="md"
            bg="white"
          >
            <HStack spacing={2} justify="space-between" align="baseline">
              <Text fontSize="12px" fontWeight="800" color="brand.ink">{o.aspect}</Text>
              <Badge fontSize="9px" colorScheme={o.derived ? 'green' : 'purple'} variant="subtle" flex="none">
                {o.derived ? 'free' : 'generated'}
              </Badge>
            </HStack>
            <Text fontSize="10px" mt={0.5} color="brand.muted">
              {o.derived && source
                ? `${aspectWord(o.aspect)} — cropped scene by scene from the ${source.aspect}`
                : `${aspectWord(o.aspect)} — its own four scenes`}
            </Text>
          </Box>
        ))}
      </Flex>
      <Text fontSize="xs" color="brand.muted" mt={3}>
        Three lifestyle scenes, then a closing product shot. Titles and overlays come from the
        product&apos;s own data. A scene that fails its checks holds the reel until you regenerate that scene.
      </Text>
    </Box>
  );
}
